// === File: src/components/Board.js ===
// Board.js - Chess Tile Slider Main Board Component
// Purpose: Renders the 8x8 chessboard grid (squares only), overlays all static tiles/highlights via TileLayer (grid), and overlays all animated/effect overlays via TileOverlayLayer (absolute overlay).
// Architecture: Portal-based rendering, responsive to PC/mobile via CSS modules, exposes board resize via ref.
// Documentation: See docs/requirements.md and docs/technical-specification.md for architectural and behavioral requirements.
// Last updated: 2025-07-28
// === Section: Board Component (Portal-Based Rendering) ===

import React, { useRef, useLayoutEffect, useState } from 'react';
import pcModule from './Board.pc.module.css';
import mobileModule from './Board.mobile.module.css';
import useSelectedCssModule from '../hooks/useSelectedCssModule';
import Square from './Square';
import TileLayer from './TileLayer';
import TileOverlayLayer from './TileOverlayLayer';
import { logDomEvent } from '../utils/spaLogger';

// === Board Component ===
function Board({
  inPlaySquares,
  inPlayTiles,
  invalidMoveSquare,
  handleSquareClick,
  captureHighlights = [],
  showSolvedIndicator,
  showFileRank,
  awaitingTileRemoval,
  emptySquare,
  shuffleMode,
  boardRect: externalBoardRect,
  squareSize: externalSquareSize,
  solveMovesActive,
  showMoveHints,
  getSolveMoveTileId,
  boardResizeRef
}) {
  const boardRef = useRef(null);
  const [boardRect, setBoardRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [squareSize, setSquareSize] = useState(0);
  const [vortexOverlays, setVortexOverlays] = useState([]);
  const styles = useSelectedCssModule(pcModule, mobileModule);

  // Update boardRect and squareSize on mount, resize, and via external trigger
  const updateRect = React.useCallback(() => {
    if (boardRef.current) {
      const rect = boardRef.current.getBoundingClientRect();
      setBoardRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
      setSquareSize(rect.width / 8);
    }
  }, []);

  useLayoutEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [updateRect]);

  // Expose updateRect to parent via ref
  React.useImperativeHandle(
    boardResizeRef,
    () => ({
      forceBoardResize: updateRect
    }),
    [updateRect]
  );

  if (!inPlaySquares || !inPlayTiles) return null;

  // Build board matrix for rendering (no tile highlight logic)
  const boardMatrix = [];
  for (let r = 8; r >= 1; r--) {
    for (let f = 0; f < 8; f++) {
      const fileRank = String.fromCharCode(97 + f) + r;
      const color = inPlaySquares[fileRank]?.color;
      boardMatrix.push({ fileRank, color });
    }
  }

  // Determine if mobile (viewport < 789px)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 789;

  // Shimmer overlay region calculation
  let shimmerLeft, shimmerWidth, shimmerTop, shimmerHeight;
  const normalizedShuffleMode = (typeof shuffleMode === 'string' ? shuffleMode : '').toLowerCase();
  if (normalizedShuffleMode === 'easy') {
    // Easy mode: files b-g (12.5% left, 75% width), ranks 3-6 (25% top, 50% height)
    shimmerLeft = '12.5%';
    shimmerWidth = '75%';
    shimmerTop = '25%';
    shimmerHeight = '50%';
  } else if (normalizedShuffleMode === 'hard') {
    // Hard mode: files a-h (0% left, 100% width), ranks 3-6 (25% top, 50% height)
    shimmerLeft = '0%';
    shimmerWidth = '100%';
    shimmerTop = '25%';
    shimmerHeight = '50%';
  } else {
    // Default/fallback: no shimmer
    shimmerLeft = undefined;
    shimmerWidth = undefined;
    shimmerTop = undefined;
    shimmerHeight = undefined;
  }

  // Logger Integration: handleSquareClick Wrapper
  const handleSquareClickWithLog = async (fileRank) => {
    const boardElem = boardRef.current;
    const boardHtml = boardElem ? boardElem.outerHTML : '';
    // Use className or tagName for element, and parent className/tagName for parent
    const element = boardElem ? (boardElem.className || boardElem.tagName) : 'unknown';
    const parent = boardElem && boardElem.parentElement ? (boardElem.parentElement.className || boardElem.parentElement.tagName) : 'unknown';
    await logDomEvent('Board', `Board square ${fileRank}`, element, boardHtml, boardElem, parent);
    if (typeof handleSquareClick === 'function') {
      handleSquareClick(fileRank);
    }
  };

  // TileLayer overlay container class (matches board size)
  // See .tileLayerContainer in Board.pc.module.css and Board.mobile.module.css

  return (
    <div className={styles.boardContainer} ref={boardRef}>
      {awaitingTileRemoval && shimmerLeft && shimmerWidth && shimmerTop && shimmerHeight && (
        <div
          className={styles.colorTileRegionShimmer}
          style={{
            position: 'absolute',
            left: shimmerLeft,
            top: shimmerTop,
            width: shimmerWidth,
            height: shimmerHeight,
            pointerEvents: 'none',
            zIndex: 20,
            boxSizing: 'border-box',
            borderRadius: '8px'
          }}
        />
      )}
      <div className={styles.board}>
        <div className={styles.squaresContainer}>
          {boardMatrix.map(({ fileRank, color }) => (
            <Square
              key={fileRank}
              fileRank={fileRank}
              color={color}
              highlight={false}
              handleSquareClick={handleSquareClickWithLog}
              showFileRank={showFileRank}
              invalidMoveSquare={invalidMoveSquare}
            />
          ))}
        </div>
        {/* === Portal-Based Overlays: TileLayer (static, grid) + TileOverlayLayer (animated/effects, absolute) === */}
        <div className={styles.tileLayerContainer}>
          <TileLayer
            inPlaySquares={inPlaySquares}
            inPlayTiles={inPlayTiles}
            emptySquare={emptySquare}
            boardRect={externalBoardRect || boardRect}
            squareSize={externalSquareSize || squareSize}
            onTileClick={handleSquareClickWithLog}
            showSolvedIndicator={showSolvedIndicator}
            solveMovesActive={solveMovesActive}
            showMoveHints={showMoveHints}
            getSolveMoveTileId={getSolveMoveTileId}
            captureHighlights={captureHighlights}
            showFileRankLabels={showFileRank}
            setVortexOverlays={setVortexOverlays}
          />
          <TileOverlayLayer
            animatedOverlays={vortexOverlays}
            solvedIndicators={[]}
            effects={[]}
          />
        </div>
      </div>
    </div>
  );
}

// Export with forwardRef to expose boardResizeRef for parent control
export default React.forwardRef((props, ref) => <Board {...props} boardResizeRef={ref} />);

// === End Section: Board Component (Portal-Based Rendering) ===